import { useCallback, useEffect, useState } from "react";
import { api, apiError, rupiah } from "@/lib/api";
import { ErpLayout } from "@/erp/shell";
import { MediaUploadField } from "@/erp/MediaUploadField";
import { toast } from "sonner";
import { Plus, Trash2, Upload, X, Users, PlayCircle, FileText } from "lucide-react";

const inp = "w-full rounded-xl border border-[#E5DEC9] px-3 py-2 text-sm outline-none focus:border-[#C5A028]";
const lbl = "mb-1 block text-xs font-bold uppercase tracking-wider text-[#4A4844]";

const emptyCourse = {
  title: "", subtitle: "", description: "", cover_image: "", level: "Dasar",
  access_type: "entitlement", price: 0, status: "draft", has_certificate: true,
  passing_score: 70, sort_order: 0,
};

export default function ECourseAdmin() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [enrollments, setEnrollments] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/courses").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form, price: Number(form.price) || 0, passing_score: Number(form.passing_score) || 70, sort_order: Number(form.sort_order) || 0 };
      if (form.id) await api.patch(`/admin/courses/${form.id}`, body);
      else await api.post("/admin/courses", body);
      toast.success("Kursus disimpan"); setForm(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const remove = async (c) => {
    try { await api.delete(`/admin/courses/${c.id}`); toast.success("Kursus dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openDetail = async (c) => {
    try {
      const r = await api.get(`/admin/courses/${c.id}/detail`);
      setDetail(r.data); setEnrollments(null);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openEnrollments = async (c) => {
    try {
      const r = await api.get(`/admin/courses/${c.id}/enrollments`);
      setEnrollments({ course: c, rows: r.data }); setDetail(null);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <ErpLayout title="E-Course" breadcrumb={["E-Course"]}
      actions={
        <button onClick={() => setForm({ ...emptyCourse })} data-testid="new-course-btn"
          className="btn-gold flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold">
          <Plus size={16} /> Kursus Baru
        </button>}>
      <div className="overflow-x-auto rounded-2xl border border-[#E5DEC9] bg-white">
        <table className="w-full min-w-[720px] text-sm" data-testid="course-table">
          <thead className="bg-[#F4EFE6] text-left text-xs uppercase tracking-wider text-[#4A4844]">
            <tr>
              <th className="p-4">Kursus</th><th className="p-4">Akses</th><th className="p-4">Harga</th>
              <th className="p-4">Pelajaran</th><th className="p-4">Peserta</th><th className="p-4">Status</th><th className="p-4">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {rows === null ? <tr><td className="p-6 text-[#8A867C]" colSpan={7}>Memuat...</td></tr>
              : rows.length === 0 ? <tr><td className="p-6 text-[#8A867C]" colSpan={7}>Belum ada kursus.</td></tr>
                : rows.map((c) => (
                  <tr key={c.id} className="border-t border-[#F4EFE6]" data-testid={`course-row-${c.id}`}>
                    <td className="p-4 font-semibold text-[#141414]">{c.title}<div className="text-xs font-normal text-[#8A867C]">{c.level}</div></td>
                    <td className="p-4">{c.access_type}</td>
                    <td className="p-4">{c.price > 0 ? rupiah(c.price) : "-"}</td>
                    <td className="p-4">{c.lesson_count}</td>
                    <td className="p-4">{c.student_count}</td>
                    <td className="p-4">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${c.status === "published" ? "bg-[#F0FDF4] text-[#15803D]" : "bg-[#F4EFE6] text-[#8A867C]"}`}>{c.status}</span>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => openDetail(c)} data-testid={`manage-content-btn-${c.id}`}
                          className="rounded-lg border border-[#141414] px-3 py-1.5 text-xs font-semibold text-[#141414]">Materi</button>
                        <button onClick={() => setForm(c)} data-testid={`edit-course-btn-${c.id}`}
                          className="rounded-lg border border-[#E5DEC9] px-3 py-1.5 text-xs font-semibold text-[#4A4844]">Edit</button>
                        <button onClick={() => openEnrollments(c)} data-testid={`enrollments-btn-${c.id}`}
                          className="rounded-lg border border-[#E5DEC9] px-3 py-1.5 text-xs font-semibold text-[#4A4844]">Peserta</button>
                        <button onClick={() => remove(c)} data-testid={`delete-course-btn-${c.id}`}
                          className="rounded-lg border border-[#FCA5A5] px-3 py-1.5 text-xs font-semibold text-[#B91C1C]">Hapus</button>
                      </div>
                    </td>
                  </tr>))}
          </tbody>
        </table>
      </div>

      {form && (
        <Modal title={form.id ? "Edit Kursus" : "Kursus Baru"} onClose={() => setForm(null)} testid="course-form-modal">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="sm:col-span-2"><span className={lbl}>Judul</span>
              <input className={inp} value={form.title} data-testid="course-title-input"
                onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
            <label className="sm:col-span-2"><span className={lbl}>Subjudul</span>
              <input className={inp} value={form.subtitle}
                onChange={(e) => setForm({ ...form, subtitle: e.target.value })} /></label>
            <label className="sm:col-span-2"><span className={lbl}>Deskripsi</span>
              <textarea rows={4} className={inp} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
            <div className="sm:col-span-2">
              <MediaUploadField label="Gambar Cover" testId="course-cover" context="course"
                value={form.cover_image} onChange={(url) => setForm({ ...form, cover_image: url })}
                help="Cover kursus disimpan persisten di Object Storage." />
            </div>
            <label><span className={lbl}>Level</span>
              <input className={inp} value={form.level}
                onChange={(e) => setForm({ ...form, level: e.target.value })} /></label>
            <label><span className={lbl}>Tipe Akses</span>
              <select className={inp} value={form.access_type} data-testid="course-access-select"
                onChange={(e) => setForm({ ...form, access_type: e.target.value })}>
                <option value="entitlement">Gratis untuk jamaah (pembayaran terverifikasi)</option>
                <option value="paid">Berbayar terpisah</option>
                <option value="both">Gratis jamaah + bisa dibeli umum</option>
              </select></label>
            <label><span className={lbl}>Harga (IDR)</span>
              <input type="number" className={inp} value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })} /></label>
            <label><span className={lbl}>Nilai Lulus Kuis</span>
              <input type="number" className={inp} value={form.passing_score}
                onChange={(e) => setForm({ ...form, passing_score: e.target.value })} /></label>
            <label><span className={lbl}>Status</span>
              <select className={inp} value={form.status} data-testid="course-status-select"
                onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="draft">Draft</option><option value="published">Published</option>
              </select></label>
            <label className="flex items-center gap-2 pt-6 text-sm text-[#4A4844]">
              <input type="checkbox" checked={form.has_certificate}
                onChange={(e) => setForm({ ...form, has_certificate: e.target.checked })} />
              Terbitkan sertifikat
            </label>
          </div>
          <button onClick={save} disabled={busy || !form.title} data-testid="save-course-btn"
            className="btn-gold mt-6 w-full rounded-xl py-3 text-sm font-bold disabled:opacity-60">Simpan</button>
        </Modal>
      )}

      {detail && <ContentManager detail={detail} onClose={() => setDetail(null)}
        reload={async () => {
          const r = await api.get(`/admin/courses/${detail.id}/detail`); setDetail(r.data); load();
        }} />}

      {enrollments && (
        <Modal title={`Peserta — ${enrollments.course.title}`} onClose={() => setEnrollments(null)} testid="enrollments-modal">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead className="bg-[#F4EFE6] text-left text-xs uppercase tracking-wider text-[#4A4844]">
                <tr><th className="p-3">Peserta</th><th className="p-3">Sumber</th><th className="p-3">Progres</th><th className="p-3">Kuis</th><th className="p-3">Sertifikat</th></tr>
              </thead>
              <tbody>
                {enrollments.rows.length === 0 ? <tr><td className="p-4 text-[#8A867C]" colSpan={5}>Belum ada peserta.</td></tr>
                  : enrollments.rows.map((e) => (
                    <tr key={e.id} className="border-t border-[#F4EFE6]">
                      <td className="p-3">{e.student_name}<div className="text-xs text-[#8A867C]">{e.student_email}</div></td>
                      <td className="p-3">{e.source}</td>
                      <td className="p-3">{e.progress_percent}%</td>
                      <td className="p-3">{e.quiz_passed ? `Lulus (${e.quiz_score})` : e.quiz_score || "-"}</td>
                      <td className="p-3">{e.certificate_number || "-"}</td>
                    </tr>))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}
    </ErpLayout>
  );
}

const Modal = ({ title, children, onClose, testid }) => (
  <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 py-10" data-testid={testid}>
    <div className="w-full max-w-3xl rounded-2xl bg-white p-6">
      <div className="flex items-start justify-between gap-4">
        <h2 className="font-display text-xl font-bold text-[#141414]">{title}</h2>
        <button onClick={onClose} data-testid="close-modal-btn" className="rounded-lg p-1 text-[#8A867C] hover:bg-[#F4EFE6]"><X size={18} /></button>
      </div>
      <div className="mt-5">{children}</div>
    </div>
  </div>
);

function ContentManager({ detail, onClose, reload }) {
  const [moduleTitle, setModuleTitle] = useState("");
  const [lesson, setLesson] = useState(null);
  const [quiz, setQuiz] = useState(detail.quiz_full
    ? { title: detail.quiz_full.title, questions: detail.quiz_full.questions }
    : { title: "Kuis Akhir", questions: [{ question: "", options: ["", ""], correct_index: 0 }] });
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);

  const addModule = async () => {
    if (!moduleTitle.trim()) return;
    try {
      await api.post(`/admin/courses/${detail.id}/modules`,
        { title: moduleTitle, sort_order: (detail.modules || []).length });
      setModuleTitle(""); toast.success("Modul ditambahkan"); reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const delModule = async (m) => {
    try { await api.delete(`/admin/modules/${m.id}`); toast.success("Modul dihapus"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const delLesson = async (l) => {
    try { await api.delete(`/admin/lessons/${l.id}`); toast.success("Pelajaran dihapus"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const uploadVideo = async (file) => {
    setBusy(true); setProgress(0);
    try {
      const init = await api.post("/admin/course-videos/init",
        { file_name: file.name, total_size: file.size, content_type: file.type || "video/mp4" });
      const { upload_id, chunk_size } = init.data;
      let index = 0;
      for (let start = 0; start < file.size; start += chunk_size) {
        const fd = new FormData();
        fd.append("index", index);
        fd.append("chunk", file.slice(start, start + chunk_size), `${file.name}.part${index}`);
        const r = await api.post(`/admin/course-videos/${upload_id}/chunk`, fd);
        setProgress(r.data.percent);
        index += 1;
      }
      const done = await api.post(`/admin/course-videos/${upload_id}/complete`);
      setLesson((l) => ({ ...l, video_file_id: done.data.file_id, video_file_name: done.data.file_name }));
      toast.success("Video berhasil diunggah");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const saveLesson = async () => {
    setBusy(true);
    try {
      const body = {
        module_id: lesson.module_id, title: lesson.title, content: lesson.content || "",
        duration_minutes: Number(lesson.duration_minutes) || 0,
        is_preview: !!lesson.is_preview, sort_order: Number(lesson.sort_order) || 0,
        video_file_id: lesson.video_file_id || null,
      };
      if (lesson.id) await api.patch(`/admin/lessons/${lesson.id}`, body);
      else await api.post(`/admin/courses/${detail.id}/lessons`, body);
      toast.success("Pelajaran disimpan"); setLesson(null); reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const saveQuiz = async () => {
    const clean = quiz.questions.map((q) => ({
      question: (q.question || "").trim(),
      options: q.options.map((o) => (o || "").trim()).filter(Boolean),
      correct_index: Number(q.correct_index) || 0,
    }));
    if (clean.length === 0) { toast.error("Tambahkan minimal satu soal"); return; }
    if (clean.some((q) => !q.question)) { toast.error("Setiap soal wajib memiliki pertanyaan"); return; }
    if (clean.some((q) => q.options.length < 2)) { toast.error("Setiap soal minimal punya 2 pilihan jawaban"); return; }
    if (clean.some((q) => q.correct_index >= q.options.length)) {
      toast.error("Pilih jawaban benar untuk setiap soal"); return;
    }
    setBusy(true);
    try {
      await api.put(`/admin/courses/${detail.id}/quiz`, { title: quiz.title, questions: clean });
      toast.success("Kuis disimpan"); reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal title={`Materi — ${detail.title}`} onClose={onClose} testid="content-manager-modal">
      <div className="flex flex-col gap-3 sm:flex-row">
        <input className={inp} placeholder="Nama modul baru" value={moduleTitle} data-testid="module-title-input"
          onChange={(e) => setModuleTitle(e.target.value)} />
        <button onClick={addModule} data-testid="add-module-btn"
          className="btn-gold shrink-0 rounded-xl px-5 py-2.5 text-sm font-bold">Tambah Modul</button>
      </div>

      <div className="mt-5 space-y-4">
        {(detail.modules || []).map((m, mi) => (
          <div key={m.id} className="rounded-2xl border border-[#E5DEC9]">
            <div className="flex items-center justify-between gap-3 border-b border-[#F4EFE6] bg-[#FBF9F4] px-4 py-3">
              <div className="text-sm font-bold text-[#141414]">Modul {mi + 1} · {m.title}</div>
              <div className="flex gap-2">
                <button data-testid={`add-lesson-btn-${m.id}`}
                  onClick={() => setLesson({ module_id: m.id, title: "", content: "", duration_minutes: 10, is_preview: false, sort_order: m.lessons.length })}
                  className="rounded-lg border border-[#141414] px-3 py-1.5 text-xs font-semibold text-[#141414]">+ Pelajaran</button>
                <button onClick={() => delModule(m)} data-testid={`delete-module-btn-${m.id}`}
                  className="rounded-lg border border-[#FCA5A5] px-2 py-1.5 text-xs text-[#B91C1C]"><Trash2 size={13} /></button>
              </div>
            </div>
            <ul>
              {m.lessons.map((l) => (
                <li key={l.id} className="flex items-center justify-between gap-3 border-b border-[#F4EFE6] px-4 py-3 text-sm">
                  <span className="flex items-center gap-2 text-[#141414]">
                    {l.has_video ? <PlayCircle size={14} className="text-[#C5A028]" /> : <FileText size={14} className="text-[#C5A028]" />}
                    {l.title} <span className="text-xs text-[#8A867C]">({l.duration_minutes} mnt)</span>
                  </span>
                  <span className="flex gap-2">
                    <button onClick={() => setLesson({ ...l, module_id: m.id })} data-testid={`edit-lesson-btn-${l.id}`}
                      className="rounded-lg border border-[#E5DEC9] px-3 py-1 text-xs font-semibold text-[#4A4844]">Edit</button>
                    <button onClick={() => delLesson(l)} data-testid={`delete-lesson-btn-${l.id}`}
                      className="rounded-lg border border-[#FCA5A5] px-2 py-1 text-xs text-[#B91C1C]"><Trash2 size={13} /></button>
                  </span>
                </li>
              ))}
              {m.lessons.length === 0 && <li className="px-4 py-3 text-sm text-[#8A867C]">Belum ada pelajaran.</li>}
            </ul>
          </div>
        ))}
      </div>

      {lesson && (
        <div className="mt-6 rounded-2xl border border-[#C5A028] bg-[#FBF9F4] p-5" data-testid="lesson-form">
          <h3 className="font-display text-lg font-bold text-[#141414]">{lesson.id ? "Edit" : "Tambah"} Pelajaran</h3>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className="sm:col-span-2"><span className={lbl}>Judul</span>
              <input className={inp} value={lesson.title} data-testid="lesson-title-input"
                onChange={(e) => setLesson({ ...lesson, title: e.target.value })} /></label>
            <label className="sm:col-span-2"><span className={lbl}>Materi (teks)</span>
              <textarea rows={4} className={inp} value={lesson.content || ""} data-testid="lesson-content-input"
                onChange={(e) => setLesson({ ...lesson, content: e.target.value })} /></label>
            <label><span className={lbl}>Durasi (menit)</span>
              <input type="number" className={inp} value={lesson.duration_minutes}
                onChange={(e) => setLesson({ ...lesson, duration_minutes: e.target.value })} /></label>
            <label className="flex items-center gap-2 pt-6 text-sm text-[#4A4844]">
              <input type="checkbox" checked={!!lesson.is_preview} data-testid="lesson-preview-checkbox"
                onChange={(e) => setLesson({ ...lesson, is_preview: e.target.checked })} />
              Bisa ditonton gratis (preview)
            </label>
            <div className="sm:col-span-2">
              <span className={lbl}>Video Pelajaran (unggah file, maks 500MB)</span>
              <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-[#C5A028] bg-white px-4 py-3 text-sm text-[#4A4844]">
                <Upload size={16} className="text-[#C5A028]" />
                {lesson.video_file_name || "Pilih file video (mp4/webm)"}
                <input type="file" accept="video/*" className="hidden" data-testid="lesson-video-input"
                  onChange={(e) => e.target.files?.[0] && uploadVideo(e.target.files[0])} />
              </label>
              {busy && progress > 0 && (
                <div className="mt-2" data-testid="upload-progress">
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#F4EFE6]">
                    <div className="h-full bg-[#C5A028]" style={{ width: `${progress}%` }} />
                  </div>
                  <div className="mt-1 text-xs text-[#8A867C]">Mengunggah {progress}%</div>
                </div>
              )}
            </div>
          </div>
          <div className="mt-5 flex gap-3">
            <button onClick={saveLesson} disabled={busy || !lesson.title} data-testid="save-lesson-btn"
              className="btn-gold rounded-xl px-5 py-2.5 text-sm font-bold disabled:opacity-60">Simpan Pelajaran</button>
            <button onClick={() => setLesson(null)} className="rounded-xl border border-[#E5DEC9] px-5 py-2.5 text-sm font-semibold text-[#4A4844]">Batal</button>
          </div>
        </div>
      )}

      <div className="mt-8 rounded-2xl border border-[#E5DEC9] p-5" data-testid="quiz-editor">
        <h3 className="font-display text-lg font-bold text-[#141414]">Kuis Akhir</h3>
        <label className="mt-3 block"><span className={lbl}>Judul Kuis</span>
          <input className={inp} value={quiz.title} onChange={(e) => setQuiz({ ...quiz, title: e.target.value })} /></label>
        <div className="mt-4 space-y-5">
          {quiz.questions.map((q, qi) => {
            const qKey = q.id || q.uid || `q-${qi}`;
            return (
            <div key={qKey} className="rounded-xl border border-[#F4EFE6] p-4">
              <label className="block"><span className={lbl}>Soal {qi + 1}</span>
                <input className={inp} value={q.question} data-testid={`quiz-question-${qi}`}
                  onChange={(e) => {
                    const qs = [...quiz.questions]; qs[qi] = { ...q, question: e.target.value };
                    setQuiz({ ...quiz, questions: qs });
                  }} /></label>
              <div className="mt-3 space-y-2">
                {q.options.map((o, oi) => (
                  <div key={`${qKey}-opt-${oi}`} className="flex items-center gap-2">
                    <input type="radio" checked={Number(q.correct_index) === oi}
                      onChange={() => {
                        const qs = [...quiz.questions]; qs[qi] = { ...q, correct_index: oi };
                        setQuiz({ ...quiz, questions: qs });
                      }} data-testid={`quiz-correct-${qi}-${oi}`} />
                    <input className={inp} value={o} placeholder={`Pilihan ${oi + 1}`}
                      data-testid={`quiz-option-${qi}-${oi}`}
                      onChange={(e) => {
                        const qs = [...quiz.questions]; const opts = [...q.options];
                        opts[oi] = e.target.value; qs[qi] = { ...q, options: opts };
                        setQuiz({ ...quiz, questions: qs });
                      }} />
                  </div>
                ))}
                <button onClick={() => {
                  const qs = [...quiz.questions];
                  qs[qi] = { ...q, options: [...q.options, ""] };
                  setQuiz({ ...quiz, questions: qs });
                }} className="text-xs font-semibold text-[#C5A028]">+ Tambah pilihan</button>
              </div>
            </div>
            );
          })}
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          <button onClick={() => setQuiz({ ...quiz, questions: [...quiz.questions, { uid: `q-${Date.now()}`, question: "", options: ["", ""], correct_index: 0 }] })}
            data-testid="add-question-btn"
            className="rounded-xl border border-[#141414] px-4 py-2 text-sm font-semibold text-[#141414]">+ Tambah Soal</button>
          <button onClick={saveQuiz} disabled={busy} data-testid="save-quiz-btn"
            className="btn-gold rounded-xl px-5 py-2 text-sm font-bold disabled:opacity-60">Simpan Kuis</button>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-2 text-xs text-[#8A867C]">
        <Users size={14} /> Peserta hanya bisa membuka materi setelah memiliki akses (pembayaran terverifikasi atau membeli kursus).
      </div>
    </Modal>
  );
}
